import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { cn } from '@/lib/utils';
import { ENGAGEMENT_LOCATION_HELP, contentTypeMeta } from '../lib/engagementColors';
import { ContentTypeHourHeatmap } from './ContentTypeHourHeatmap';
import {
  EngagementByContentTypeSection,
  type EngagementTypeRow,
} from './EngagementByContentTypeCard';
import { EngagementChartHeader } from './EngagementChartFilters';
import { EngagementBusy } from './EngagementNotReady';

export type ContentTypeSlice = {
  type: string;
  label: string;
  count: number;
  percentage: number;
  delta: number | null;
  hourCounts?: number[];
};

const compact = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Figma Total Content + Engagement by Content Type in one card. Date, country
 * and timezone pickers are page-level: changing them here updates every module.
 */
export function TotalContentCard({
  slices,
  engagementRows,
  range,
  onRangeChange,
  startDate,
  endDate,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes,
  timezone,
  onTimezoneChange,
  isLoading,
}: {
  slices: ContentTypeSlice[];
  engagementRows: EngagementTypeRow[];
  range: string;
  onRangeChange: (range: string) => void;
  startDate?: string;
  endDate?: string;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  isLoading?: boolean;
}) {
  const isDark = useIsDarkMode();
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);

  const option = useMemo(() => {
    const muted = tone(CHART_INK.muted, isDark);
    const surface = tone(CHART_INK.surface, isDark);
    const visible = slices.filter((slice) => slice.count > 0);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: surface,
        borderColor: tone(CHART_INK.grid, isDark),
        textStyle: { color: tone(CHART_INK.primary, isDark) },
        valueFormatter: (value: number | string) => Number(value).toLocaleString(),
      },
      title: [
        {
          text: 'Total',
          left: 'center',
          top: '38%',
          textStyle: { color: muted, fontSize: 11, fontWeight: 400 },
        },
        {
          text: compact.format(total),
          left: 'center',
          top: '45%',
          textStyle: { color: '#ffffff', fontSize: 26, fontWeight: 700 },
        },
        {
          text: 'Content',
          left: 'center',
          top: '60%',
          textStyle: { color: muted, fontSize: 11, fontWeight: 400 },
        },
      ],
      series: [
        {
          name: 'Content type',
          type: 'pie',
          radius: ['62%', '92%'],
          center: ['50%', '52%'],
          avoidLabelOverlap: true,
          itemStyle: { borderWidth: 0 },
          label: {
            show: true,
            position: 'inside',
            formatter: ({ data }: { data: { percentage: number } }) =>
              data.percentage >= 4 ? `${Math.round(data.percentage)}%` : '',
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 700,
          },
          labelLine: { show: false },
          emphasis: { scale: true, scaleSize: 4 },
          data: visible.map((slice) => ({
            name: slice.label,
            value: slice.count,
            percentage: slice.percentage,
            itemStyle: { color: contentTypeMeta(slice.type).color },
          })),
        },
      ],
    };
  }, [isDark, slices, total]);

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="p-5 pb-3">
        <EngagementChartHeader
          title="Total Content"
          infoHint={ENGAGEMENT_LOCATION_HELP.connectCountry}
          range={range}
          onRangeChange={onRangeChange}
          {...(startDate ? { startDate } : {})}
          {...(endDate ? { endDate } : {})}
          countryCode={countryCode}
          onCountryCodeChange={onCountryCodeChange}
          {...(availableCountryCodes ? { availableCountryCodes } : {})}
        />
      </CardHeader>
      <CardContent className="space-y-5 p-5 pt-0">
        {isLoading ? (
          <EngagementBusy label="Loading content…" />
        ) : (
          <>
        <div className="grid items-center gap-6 lg:grid-cols-[minmax(190px,0.85fr)_minmax(240px,1.15fr)]">
          {total === 0 ? (
            <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
              No content published in this window.
            </div>
          ) : (
            <ReactECharts option={option} notMerge style={{ height: 240, width: '100%' }} />
          )}
          <ul className="space-y-3">
            {slices.map((slice) => {
              const { icon: Icon, color } = contentTypeMeta(slice.type);
              const rising = (slice.delta ?? 0) >= 0;
              const DeltaIcon = rising ? ArrowUp : ArrowDown;
              return (
                <li key={slice.type}>
                  <div className="flex items-center gap-3">
                    <span
                      className="flex size-8 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${color}1f` }}
                    >
                      <Icon className="size-4" style={{ color }} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-3">
                        <span className="truncate text-sm font-semibold">{slice.label}</span>
                        <span className="shrink-0 text-sm font-bold tabular-nums">
                          {compact.format(slice.count)}
                        </span>
                      </span>
                      <span className="mt-1 block h-1 overflow-hidden rounded-full bg-white/10">
                        <span
                          className="block h-full rounded-full"
                          style={{
                            backgroundColor: color,
                            width: `${Math.min(Math.max(slice.percentage, 0), 100)}%`,
                          }}
                        />
                      </span>
                      <span className="mt-1 flex items-center justify-between gap-3 text-[11px]">
                        <span className="text-muted-foreground tabular-nums">
                          {Math.round(slice.percentage)}%
                        </span>
                        {slice.delta == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={cn(
                              'inline-flex items-center gap-0.5 font-semibold tabular-nums',
                              rising ? 'text-emerald-400' : 'text-rose-400',
                            )}
                          >
                            <DeltaIcon className="size-3" aria-hidden />
                            {Math.abs(slice.delta).toFixed(1)}%
                          </span>
                        )}
                      </span>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <ContentTypeHourHeatmap
          slices={slices}
          title="Heatmap of published content by publish time. Brighter cells mean more posts were published in that slot."
          valueNoun="published"
          timezone={timezone}
          onTimezoneChange={onTimezoneChange}
          ariaLabel="Change content timezone"
        />
        <EngagementByContentTypeSection
          rows={engagementRows}
          timezone={timezone}
          onTimezoneChange={onTimezoneChange}
        />
          </>
        )}
      </CardContent>
    </Card>
  );
}
